"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import { SearchIcon } from "lucide-react"

import { getKeywordSuggestions } from "@/lib/client/api/search"
import { cn } from "@/lib/utils"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { LocationSelector } from "@/features/location-selector"

import { useRecentlySearchedStore } from "./recently-searched-store"
import { RecentlySearches } from "./recently-searches"

// Add HotKeywords component as a prop
export function GlobalSearch({
  hotKeywords,
}: {
  hotKeywords: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const addKeyword = useRecentlySearchedStore((state) => state.addKeyword)
  const params = useSearchParams()
  const [keyword, setKeyword] = useState(params.get("keyword") || "")
  const router = useRouter()
  const [debouncedKeyword] = useDebouncedValue(keyword, 500)

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      searchInputRef.current &&
      !searchInputRef.current.contains(event.target as Node)
    ) {
      setOpen(false)
    }
  }, [])

  const { data } = useQuery({
    queryKey: ["keyword-suggestions", debouncedKeyword],
    queryFn: () => getKeywordSuggestions(debouncedKeyword),
    enabled: !!debouncedKeyword,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open, handleClickOutside])

  const handleOverlayClick = useCallback(() => {
    setOpen(false)
  }, [])

  // Close search when escape key is pressed
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false)
    }
  }, [])

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey)

    // close search when user clicked on an <a> tag
    const handleClick = (event: MouseEvent) => {
      if (event.target instanceof HTMLAnchorElement) {
        setOpen(false)
        if (event.target.textContent) {
          addKeyword(event.target.textContent)
        }
      }
    }

    document.addEventListener("click", handleClick)

    return () => {
      document.removeEventListener("keydown", handleEscapeKey)
      document.removeEventListener("click", handleClick)
    }
  }, [handleEscapeKey, addKeyword])

  const isKeywordAvailable = !!keyword

  return (
    <>
      {/* Location selector */}
      <LocationSelector />

      {/* Search container */}
      <div
        className={cn(
          "flex transition-all duration-300 ease-search-button flex-1 flex-col min-w-[150px]",
          open &&
            "lg:min-w-[600px] md:min-w-[400px] sm:min-w-[300px] min-w-0 self-start"
        )}
        data-testid="global-search-wrapper"
      >
        {/* Backdrop overlay when search is open */}
        {open && (
          <div
            className="h-screen bg-[rgba(0,0,0,0.08)] z-[4] fixed top-0 left-0 right-0 overflow-auto transition-all duration-300"
            onClick={handleOverlayClick}
            aria-hidden="true"
            data-testid="global-search-overlay"
          />
        )}

        {/* Search input container */}
        <div
          role="search"
          className={cn(
            "rounded-t-2xl px-3 flex-1",
            open && "z-[4] bg-white mt-[1px] pt-3"
          )}
        >
          <label htmlFor="search" className="sr-only">
            Tìm món trên beFood
          </label>
          <div className="transition-[min-width,max-width] duration-400 ease-linear bg-[#f3f3f3] h-9 flex items-center rounded-full font-medium text-sm text-gray-900">
            <div className="ml-4 mr-3 shrink-0 self-center">
              <SearchIcon className="h-4 w-4" />
            </div>
            <input
              ref={searchInputRef}
              type="search"
              name="search"
              id="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Tìm món trên beFood"
              className="w-full py-2 pr-2 border-none focus:outline-none focus:ring-0 overflow-ellipsis outline-none text-gray-900 placeholder:text-gray-900 bg-transparent"
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && keyword.trim()) {
                  router.push(
                    `/search?keyword=${encodeURIComponent(keyword)}&type=dish`
                  )
                  addKeyword(keyword)
                  setOpen(false)
                }
              }}
            />
          </div>
        </div>

        {/* Search results dropdown */}
        <div
          ref={dropdownRef}
          className={cn(
            "w-0 invisible max-h-[0] translate-0 rounded-b-2xl border border-transparent transition-[opacity,visibility] duration-300 ease-search-button overflow-hidden",
            open &&
              "visible opacity-100 max-h-[calc(100vh-180px)] w-full bg-white z-10 shadow-lg overflow-y-auto"
          )}
        >
          <div className="px-3 pt-4 pb-4">
            {isKeywordAvailable && (
              <>
                {data && data.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Gợi ý tìm kiếm</h3>
                    <ul className="space-y-2">
                      {data.map((suggestion: any) => (
                        <li key={suggestion.text}>
                          <Link
                            href={`/search?keyword=${encodeURIComponent(
                              suggestion.text
                            )}&type=${suggestion.name_type === "restaurant" ? "restaurant" : "dish"}`}
                            className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                            onClick={() => {
                              addKeyword(suggestion.text)
                              setOpen(false)
                            }}
                          >
                            <SearchIcon className="h-4 w-4 mr-3 text-gray-500" />
                            <span>{suggestion.text}</span>
                          </Link>
                        </li>
                      ))}

                      <li key={keyword}>
                        <Link
                          href={`/search?keyword=${encodeURIComponent(
                            keyword
                          )}&type=dish`}
                          className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                          onClick={() => {
                            addKeyword(keyword)
                            setOpen(false)
                          }}
                        >
                          <SearchIcon className="h-4 w-4 mr-3 text-gray-500" />
                          <span>
                            Tìm kiếm &quot;
                            <span className="text-blue-600">{keyword}</span>
                            &quot;
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </div>
                )}
              </>
            )}

            {!isKeywordAvailable && (
              <>
                <RecentlySearches />
                <div className="mt-4">
                  <h3 className="text-sm font-medium mb-2">
                    Tìm kiếm phổ biến
                  </h3>
                  {/* Render the server component for hot keywords */}
                  {hotKeywords}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
